"use client";

import React, { useEffect, useMemo, useState, useCallback } from "react";
import styles from "../styles/EnrollmentModals.module.css";

// Utility function to calculate age from birthday
const calculateAge = (birthday) => {
  if (!birthday) return null;
  const birthDate = new Date(birthday);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Utility function to parse age range string (e.g., "8-12")
const parseAgeRange = (ages) => {
  if (!ages || typeof ages !== 'string') return { minAge: null, maxAge: null };
  const parts = ages.split('-');
  return {
    minAge: parseInt(parts[0], 10) || null,
    maxAge: parseInt(parts[1], 10) || null,
  };
};

export default function EnrollModal({
  courseImage,
  courseName,
  dateRange,
  sidebarContent,
  learners,
  selectedLearnerId = "",
  enrolledLearnerIds = [],
  onLearnerChange,
  paymentOption,
  onClose,
  onPaymentChange,
  onNext,
  onRefreshLearners,
  course,
  // Free trial props
  isFreeTrial = false,
  onFreeTrial,
  enrolling = false,
  // Coupon props
  couponCode,
  setCouponCode,
  couponValidation,
  couponLoading,
  couponError,
  onValidateCoupon,
  onClearCoupon,
  // ClassWallet props
  classwalletEnabled = false,
  onClassWalletCheckout,
  classwalletLoading = false,
  classwalletError = '',
}) {
  const hasLearners = Array.isArray(learners) && learners.length > 0;

  const totalPrice = Number(sidebarContent?.totalPrice ?? 0);
  const weeklyPrice = Number(sidebarContent?.weeklyPrice ?? 0);
  const courseDuration = Number(sidebarContent?.courseDuration ?? 0);

  // Calculate effective discount for both payment types (capped at 70%)
  const discountCalc = useMemo(() => {
    if (!couponValidation?.valid || !couponValidation?.rawDiscount) {
      return { upfront: 0, weekly: 0, upfrontCapped: false, weeklyCapped: false };
    }
    const raw = couponValidation.rawDiscount;
    const upfrontMax = totalPrice * 0.7;
    const weeklyMax = weeklyPrice * 0.7;

    const upfrontDiscount = Math.min(raw, upfrontMax);
    const weeklyDiscount = Math.min(raw, weeklyMax);

    return {
      upfront: Math.floor(upfrontDiscount * 100) / 100,
      weekly: Math.floor(weeklyDiscount * 100) / 100,
      upfrontCapped: raw > upfrontMax,
      weeklyCapped: raw > weeklyMax
    };
  }, [couponValidation, totalPrice, weeklyPrice]);

  const totalDueToday = useMemo(() => {
    const basePrice = paymentOption === "weekly" ? weeklyPrice : totalPrice;
    const discount = paymentOption === "weekly" ? discountCalc.weekly : discountCalc.upfront;
    return Math.max(0, basePrice - discount);
  }, [paymentOption, weeklyPrice, totalPrice, discountCalc]);

  const handlePaymentChange = (e) => onPaymentChange?.(e.target.value);

  const normalizedSelectedId = useMemo(() => {
    if (selectedLearnerId) return String(selectedLearnerId);
    return "";
  }, [selectedLearnerId]);

  const setSelectedId = useCallback(
    (idStr) => {
      const next = idStr ? String(idStr) : "";
      if (typeof onLearnerChange === "function") {
        onLearnerChange(next);
      }
    },
    [onLearnerChange]
  );

  useEffect(() => {
    if (!hasLearners) return;
    if (normalizedSelectedId) return;
    // Find first non-enrolled learner
    const firstAvailable = learners.find(l => !enrolledLearnerIds.includes(l.id));
    if (firstAvailable) {
      setSelectedId(String(firstAvailable.id));
    }
  }, [hasLearners, normalizedSelectedId, learners, setSelectedId, enrolledLearnerIds]);

  const selectLearner = (learnerId) => {
    setSelectedId(String(learnerId));
  };

  const weeksLabel =
    courseDuration && Number.isFinite(courseDuration)
      ? `${courseDuration} week${courseDuration === 1 ? "" : "s"}`
      : "— weeks";

  const hasPricing = (totalPrice > 0 || weeklyPrice > 0) && courseDuration >= 0;

  // Quick Add Learner
  const [newLearnerName, setNewLearnerName] = useState("");
  const [newLearnerAge, setNewLearnerAge] = useState("");
  const [isSavingLearner, setIsSavingLearner] = useState(false);

  const postLearnerToApi = async ({ name, birthYear }) => {
    const res = await fetch("/api/learners", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        name,
        birthday: `${birthYear}-01-01`,
      }),
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json().catch(() => null);
  };

  const handleQuickAddLearner = async () => {
    const nameVal = newLearnerName.trim();
    const ageNum = Number(newLearnerAge);

    if (!nameVal) return alert("Please enter a learner name.");
    if (!Number.isFinite(ageNum) || ageNum < 1 || ageNum > 18) {
      return alert("Please enter a valid age (1–18).");
    }

    const birthYear = new Date().getFullYear() - ageNum;

    try {
      setIsSavingLearner(true);
      const createdLearner = await postLearnerToApi({ name: nameVal, birthYear });

      setNewLearnerName("");
      setNewLearnerAge("");

      const fresh = await onRefreshLearners?.();

      const createdId = createdLearner?.id;
      if (createdId) {
        setSelectedId(String(createdId));
        return;
      }

      if (Array.isArray(fresh) && fresh.length) {
        setSelectedId(String(fresh[0].id));
      }
    } catch (err) {
      alert(err?.message || "Failed to add learner.");
    } finally {
      setIsSavingLearner(false);
    }
  };

  const selectedIsEnrolled = enrolledLearnerIds.includes(parseInt(normalizedSelectedId, 10));
  const canProceed = hasLearners && !!normalizedSelectedId && !selectedIsEnrolled;

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-label="Enroll in class">
      <div className={styles.modalDialog}>
        <button className={styles.modalClose} onClick={onClose} aria-label="Close">&times;</button>

        {/* Header */}
        <div className={styles.modalHeader}>
          <img src={courseImage} className={styles.courseThumbnail} alt="Course" />
          <div className={styles.courseInfo}>
            <h3>{courseName}</h3>
            <p>{dateRange}</p>
          </div>
        </div>

        <div className={styles.modalContent}>
          {/* ADD LEARNER */}
          <section className={styles.learnersSection}>
            {!hasLearners && <h4>Add your learner to continue</h4>}

            <div className={styles.quickAddRow}>
              <input
                type="text"
                placeholder="Learner name"
                value={newLearnerName}
                onChange={(e) => setNewLearnerName(e.target.value)}
                className={styles.addLearnerInput}
              />
              <input
                type="number"
                placeholder="Age"
                min="1"
                max="18"
                value={newLearnerAge}
                onChange={(e) => setNewLearnerAge(e.target.value)}
                className={styles.addLearnerAge}
              />
              <button
                type="button"
                className={styles.saveLearnerBtn}
                onClick={handleQuickAddLearner}
                disabled={isSavingLearner}
              >
                {isSavingLearner ? "Saving..." : "Save learner"}
              </button>
            </div>
          </section>

          {/* SELECT LEARNER - PILL STYLE */}
          {hasLearners && (
            <section className={styles.learnersSection}>
              <h4>Who will be attending?</h4>
              <div className={styles.learnersGrid}>
                {learners.map((lr) => {
                  const idStr = String(lr.id);
                  const isChecked = normalizedSelectedId === idStr;
                  const isEnrolled = enrolledLearnerIds.includes(lr.id);

                  return (
                    <label
                      key={lr.id}
                      className={`${styles.learnerPill} ${isChecked ? styles.learnerPillSelected : ""} ${isEnrolled ? styles.learnerPillDisabled : ""}`}
                    >
                      <input
                        type="radio"
                        name="selectedLearner"
                        checked={isChecked}
                        onChange={() => !isEnrolled && selectLearner(lr.id)}
                        className={styles.learnerCheckbox}
                        disabled={isEnrolled}
                      />
                      <span className={styles.pillCheck}>{isChecked && !isEnrolled ? "✓" : ""}</span>
                      <span className={styles.pillName}>{lr.name}</span>
                      {isEnrolled && <span className={`${styles.pillBadge} ${styles.pillBadgeEnrolled}`}>Already Enrolled</span>}
                      {isChecked && !isEnrolled && <span className={styles.pillBadge}>Selected</span>}
                    </label>
                  );
                })}
              </div>
            </section>
          )}

          {/* Age Range Warning */}
          {selectedLearnerId && (() => {
            const selectedLearner = learners.find(l => l.id.toString() === selectedLearnerId);
            if (!selectedLearner) return null;

            const learnerAge = calculateAge(selectedLearner.birthday);
            const { minAge, maxAge } = parseAgeRange(course?.ages);

            if (learnerAge === null || (minAge === null && maxAge === null)) return null;

            const isTooYoung = minAge !== null && learnerAge < minAge;
            const isTooOld = maxAge !== null && learnerAge > maxAge;

            if (!isTooYoung && !isTooOld) return null;

            return (
              <div className={styles.ageWarning}>
                Warning: {selectedLearner.name} is {learnerAge} years old, but this course is recommended for ages {course.ages}.
              </div>
            );
          })()}

          {isFreeTrial ? (
            /* FREE TRIAL MESSAGE */
            <section className={styles.freeTrialSection}>
              <h4>Try Your First Class Free</h4>
              <p className={styles.freeTrialMessage}>
                Enroll your learner for a free trial of the first class. If you enjoy it, you can continue with a paid enrollment.
              </p>
              <div className={styles.totalDue}>
                <h4>Total Due Today:</h4>
                <p>$0.00</p>
              </div>
            </section>
          ) : (
            <>
              {/* PAYMENT CARDS */}
              <section className={styles.paymentOptions}>
                <h4>Payment Options</h4>
                <div className={styles.paymentGrid}>
                  <label className={`${styles.paymentCard} ${paymentOption === "upfront" ? styles.paymentCardSelected : ""}`}>
                    <input type="radio" name="payment" value="upfront" checked={paymentOption === "upfront"} onChange={handlePaymentChange} />
                    <div className={styles.paymentCardBody}>
                      <div className={styles.paymentTopRow}>
                        <div className={styles.paymentTitle}>Pay Upfront</div>
                        <div className={styles.paymentPrice}>
                          {hasPricing ? (
                            discountCalc.upfront > 0 ? (
                              <span className={styles.priceWithDiscount}>
                                <span className={styles.originalPriceStrike}>${totalPrice.toFixed(2)}</span>
                                <span className={styles.discountedPrice}>${(totalPrice - discountCalc.upfront).toFixed(2)}</span>
                              </span>
                            ) : (
                              `$${totalPrice.toFixed(2)}`
                            )
                          ) : "—"}
                        </div>
                      </div>
                      <div className={styles.paymentSub}>Covers the full {weeksLabel}</div>
                    </div>
                  </label>

                  <label className={`${styles.paymentCard} ${paymentOption === "weekly" ? styles.paymentCardSelected : ""}`}>
                    <input type="radio" name="payment" value="weekly" checked={paymentOption === "weekly"} onChange={handlePaymentChange} />
                    <div className={styles.paymentCardBody}>
                      <div className={styles.paymentTopRow}>
                        <div className={styles.paymentTitle}>Pay Weekly</div>
                        <div className={styles.paymentPrice}>
                          {hasPricing ? (
                            discountCalc.weekly > 0 ? (
                              <span className={styles.priceWithDiscount}>
                                <span className={styles.originalPriceStrike}>${weeklyPrice.toFixed(2)}</span>
                                <span className={styles.discountedPrice}>${(weeklyPrice - discountCalc.weekly).toFixed(2)}/wk</span>
                              </span>
                            ) : (
                              `$${weeklyPrice.toFixed(2)}`
                            )
                          ) : "—"}
                        </div>
                      </div>
                      <div className={styles.paymentSub}>Over {weeksLabel}, cancel anytime</div>
                    </div>
                  </label>
                </div>
              </section>

              {/* COUPON CODE SECTION */}
              <section className={styles.couponSection}>
                <h4>Have a Promo Code?</h4>
                <div className={styles.couponInputRow}>
                  <input
                    type="text"
                    value={couponCode || ''}
                    onChange={(e) => setCouponCode?.(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className={styles.couponInput}
                    disabled={couponLoading || couponValidation?.valid}
                  />
                  {!couponValidation?.valid ? (
                    <button
                      type="button"
                      onClick={() => onValidateCoupon?.(couponCode)}
                      disabled={!couponCode?.trim() || couponLoading}
                      className={styles.applyCouponBtn}
                    >
                      {couponLoading ? 'Checking...' : 'Apply'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={onClearCoupon}
                      className={styles.clearCouponBtn}
                    >
                      Remove
                    </button>
                  )}
                </div>
                {/* Coupon Error */}
                {couponError && (
                  <p className={styles.couponError}>{couponError}</p>
                )}
                {/* Coupon Success - Dynamic message based on payment option */}
                {couponValidation?.valid && (
                  <div className={styles.couponSuccess}>
                    <span className={styles.checkmark}>&#10003;</span>
                    <span>
                      {paymentOption === "upfront"
                        ? `$${discountCalc.upfront.toFixed(2)} off your purchase`
                        : `$${discountCalc.weekly.toFixed(2)} off first week`}
                      {(paymentOption === "upfront" ? discountCalc.upfrontCapped : discountCalc.weeklyCapped) &&
                        " (max discount applied)"}
                    </span>
                  </div>
                )}
              </section>

              {/* TOTAL */}
              <section className={styles.totalDue}>
                <h4>Total Due Today:</h4>
                <p>{hasPricing ? `$${totalDueToday.toFixed(2)}` : "—"}</p>
              </section>

              {/* REFUND */}
              <section className={styles.refundPolicy}>
                <p>
                  Cancel within 24 hours of purchase to receive a refund. If the class meets within 24 hours, cancel before it begins to receive a refund. <a href="/footer/refund">Learn More</a>
                </p>
              </section>
            </>
          )}

          {/* CLASSWALLET OPTION - visible regardless of free trial status */}
          {classwalletEnabled && (
            <section className={styles.classwalletSection}>
              <div className={styles.classwalletDivider}>
                <span>or</span>
              </div>
              <div className={styles.classwalletCard}>
                <div className={styles.classwalletInfo}>
                  <div className={styles.classwalletTitle}>Pay with ClassWallet</div>
                  <div className={styles.classwalletSub}>
                    Full course price: ${totalPrice.toFixed(2)}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.classwalletBtn}
                  onClick={onClassWalletCheckout}
                  disabled={!canProceed || classwalletLoading}
                >
                  {classwalletLoading ? 'Redirecting...' : 'Pay with ClassWallet'}
                </button>
              </div>
              {classwalletError && (
                <p className={styles.classwalletError}>{classwalletError}</p>
              )}
            </section>
          )}
        </div>

        {/* FOOTER */}
        <div className={styles.modalFooter}>
          <button className={styles.btnCancel} onClick={onClose} disabled={enrolling}>Cancel</button>
          {isFreeTrial ? (
            <button
              className={styles.btnNext}
              onClick={onFreeTrial}
              disabled={!canProceed || enrolling}
            >
              {enrolling ? 'Enrolling...' : 'Start Free Trial'}
            </button>
          ) : (
            <button className={styles.btnNext} onClick={onNext} disabled={!canProceed}>Next</button>
          )}
        </div>
      </div>
    </div>
  );
}
